import { t } from "@lingui/core/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import { useClubHasMemberships } from "@/features/Club/hooks/useClubHasMemberships";
import { useClubHasPackages } from "@/features/Club/hooks/useClubHasPackages";
import {
  GiftCardsModal,
  SquareGiftCardEntryModalWrapper,
} from "@/features/GiftCards";
import {
  LoyaltyRewardsModal,
  type LoyaltyPointExpiration,
  type LoyaltyReward,
} from "@/features/Loyalty";
import { CurrentPackagesModal } from "@/features/Packages";
import { useGetPackageNeedingAttention } from "@/features/Packages/hooks/useGetPackageNeedingAttention";
import { RainchecksModal } from "@/features/Rainchecks";
import { WithRealSafeAreaInsets } from "@/hooks/useRealSafeAreaInsets";
import { ProfileStackNavigatorParamList } from "@/navigators/ProfileStackNavigator/types";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useFlags } from "@/utils/launchDarkly";
import {
  InfoIcon,
  PackagesIcon,
  ProfileIcon,
  WaitlistIcon,
  MembershipIcon,
  GiftCardIcon,
  LoyaltyIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { RainIcon } from "@whoosh/design-system/src/components/icons_v2/weather";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { openExternalLink } from "../../../../utils/openExternalLink";
import { SettingsButton } from "../SettingsButton";
import { ClubSettings_Club$key } from "./__generated__/ClubSettings_Club.graphql";
import { ClubSettings_customer$key } from "./__generated__/ClubSettings_customer.graphql";
import { BookingHistoryModal } from "./components/BookingHistoryModal";
import { ClubProfileModal } from "./components/ClubProfileModal";
import { isLoyaltyProgramVisible } from "./utils/isLoyaltyProgramVisible";

type ClubSettingsProps = {
  fragmentRef: ClubSettings_Club$key;
  customerRef: ClubSettings_customer$key;
};

const getLoyaltyRewardStatus = (
  rewardPoints: number,
  loyaltyBalance: number
): LoyaltyReward["status"] =>
  rewardPoints <= loyaltyBalance ? "AVAILABLE" : "UNAVAILABLE";

export const ClubSettings = ({
  fragmentRef,
  customerRef,
}: ClubSettingsProps) => {
  const { patronLoyalty, viewMembershipsInPatronApp } = useFlags();
  const data = useFragment(ClubSettingsFragmentString, fragmentRef);
  const customer = useFragment(ClubSettingsCustomerFragmentString, customerRef);

  const profileNavigator =
    useNavigation<NavigationProp<ProfileStackNavigatorParamList>>();

  const [isBookingHistoryOpen, setIsBookingHistoryOpen] = useState(false);
  const [isClubProfileModalOpen, setIsClubProfileModalOpen] = useState(false);

  const [isMembershipModalOpen, setIsMembershipModalOpen] = useState(false);
  const [isPackagesModalOpen, setIsPackagesModalOpen] = useState(false);

  const [isGiftCardsModalOpen, setIsGiftCardsModalOpen] = useState(false);
  const [isAddGiftCardModalOpen, setIsAddGiftCardModalOpen] = useState(false);
  const [isLoyaltyRewardsModalOpen, setIsLoyaltyRewardsModalOpen] =
    useState(false);
  const [isRainchecksModalOpen, setIsRainchecksModalOpen] = useState(false);
  const clubHasMemberships = useClubHasMemberships(data);
  const clubHasPackages = useClubHasPackages(data);

  const packageNeedingAttention = useGetPackageNeedingAttention(customer);
  const hasMembershipNeedingAttention =
    packageNeedingAttention?.packageType === "MembershipPackage";
  const hasPackageNeedingAttention =
    packageNeedingAttention?.packageType === "StandardPackage";
  const customerHasMemberships = (customer?.memberships?.count ?? 0) > 0;
  const customerHasPackages = (customer?.packages?.count ?? 0) > 0;

  const trackEvent = useTrackEvent();

  const customerHasRainchecks = (customer?.rainchecks?.count ?? 0) > 0;
  const loyaltyAccount = customer?.squareLoyaltyAccount;
  const loyaltyBalance = loyaltyAccount?.balance ?? 0;
  const loyaltyPointExpirations: LoyaltyPointExpiration[] =
    loyaltyAccount?.expiringPointDeadlines?.flatMap((expiration) =>
      expiration
        ? [
            {
              id: expiration.id,
              points: expiration.points,
              expiresAt: expiration.expiresAt,
            },
          ]
        : []
    ) ?? [];
  const pointTerminologyOne =
    data.squareLoyaltyProgram?.terminologyOne ?? t`Point`;
  const pointTerminologyOther =
    data.squareLoyaltyProgram?.terminologyOther ?? t`Points`;
  const loyaltyRewards: LoyaltyReward[] =
    data.squareLoyaltyProgram?.rewardTiers?.flatMap((rewardTier) =>
      rewardTier
        ? [
            {
              id: rewardTier.id,
              name: rewardTier.name,
              points: rewardTier.points,
              status: getLoyaltyRewardStatus(rewardTier.points, loyaltyBalance),
            },
          ]
        : []
    ) ?? [];
  const showLoyaltyProgram = isLoyaltyProgramVisible({
    patronLoyaltyEnabled: !!patronLoyalty,
    supportsPublic: !!data.supportsPublic,
    isEnrolled: !!loyaltyAccount,
    isProgramActive: data.squareLoyaltyProgram?.status === "active",
  });

  const onMembershipPress = () => {
    if (Platform.OS === "web") {
      setIsMembershipModalOpen(true);
    } else {
      // navigate to membership page
      profileNavigator.navigate("Memberships", { screen: "MembershipsList" });
    }
  };

  const onPackagesPress = () => {
    if (Platform.OS === "web") {
      setIsPackagesModalOpen(true);
    } else {
      // navigate to membership page
      profileNavigator.navigate("Packages", { screen: "PackagesList" });
    }
  };

  return (
    <>
      <VStack flex={{ md: 1 }}>
        <Box py={2}>
          <Text variant="mainSmallSemiBold" fontWeight="600">
            {data.name} {t`Settings`}
          </Text>
        </Box>

        <VStack>
          <SettingsButton
            icon={<ProfileIcon color="black" />}
            title={t`Club Profile`}
            onPress={() => setIsClubProfileModalOpen(true)}
            showDivider
          />
          <SettingsButton
            icon={<WaitlistIcon />}
            title={t`Booking History`}
            onPress={() => setIsBookingHistoryOpen(true)}
            showDivider
          />
          {viewMembershipsInPatronApp &&
          (clubHasMemberships || customerHasMemberships) ? (
            <SettingsButton
              icon={<MembershipIcon />}
              title={t`Membership`}
              onPress={onMembershipPress}
              showDivider
              showAlert={hasMembershipNeedingAttention}
            />
          ) : null}
          {viewMembershipsInPatronApp &&
          (clubHasPackages || customerHasPackages) ? (
            <SettingsButton
              icon={<PackagesIcon />}
              title={t`Packages`}
              onPress={onPackagesPress}
              showDivider
              showAlert={hasPackageNeedingAttention}
            />
          ) : null}
          {data.clubPolicyLink ? (
            <SettingsButton
              icon={<InfoIcon />}
              title={t`Club Terms and Policies`}
              onPress={() => openExternalLink(data.clubPolicyLink ?? "")}
              showDivider
            />
          ) : null}
          {data.supportsPublic ? (
            <>
              <SettingsButton
                icon={<GiftCardIcon />}
                title={t`Gift Cards`}
                onPress={() => {
                  trackEvent("CustomerProfile.GiftCards", {});
                  setIsGiftCardsModalOpen(true);
                }}
                showDivider
              />
              {showLoyaltyProgram ? (
                <SettingsButton
                  icon={<LoyaltyIcon />}
                  title={t`Rewards`}
                  onPress={() => {
                    trackEvent("CustomerProfile.LoyaltyProgram", {});
                    setIsLoyaltyRewardsModalOpen(true);
                  }}
                  showDivider
                  testID="loyalty-program-settings-button"
                />
              ) : null}
              {customerHasRainchecks ? (
                <SettingsButton
                  icon={<RainIcon />}
                  title={t`Rainchecks`}
                  onPress={() => setIsRainchecksModalOpen(true)}
                  showDivider
                />
              ) : null}
            </>
          ) : null}
        </VStack>
      </VStack>
      <BookingHistoryModal
        isOpen={isBookingHistoryOpen}
        setIsOpen={setIsBookingHistoryOpen}
        clubName={data.name}
      />
      <ClubProfileModal
        isOpen={isClubProfileModalOpen}
        setIsOpen={setIsClubProfileModalOpen}
      />
      {isGiftCardsModalOpen ? (
        <WithRealSafeAreaInsets>
          <GiftCardsModal
            isOpen={isGiftCardsModalOpen}
            setIsOpen={setIsGiftCardsModalOpen}
            setIsSquareModalOpen={setIsAddGiftCardModalOpen}
            showTotalBalance
          />
        </WithRealSafeAreaInsets>
      ) : null}
      {isRainchecksModalOpen ? (
        <WithRealSafeAreaInsets>
          <RainchecksModal
            isOpen={isRainchecksModalOpen}
            setIsOpen={setIsRainchecksModalOpen}
            showTotalBalance
          />
        </WithRealSafeAreaInsets>
      ) : null}
      <WithRealSafeAreaInsets>
        <SquareGiftCardEntryModalWrapper
          isOpen={isAddGiftCardModalOpen}
          setIsOpen={setIsAddGiftCardModalOpen}
        />
      </WithRealSafeAreaInsets>
      <LoyaltyRewardsModal
        isOpen={isLoyaltyRewardsModalOpen}
        onClose={() => setIsLoyaltyRewardsModalOpen(false)}
        currentBalance={loyaltyBalance}
        rewards={loyaltyRewards}
        pointTerminologyOne={pointTerminologyOne}
        pointTerminologyOther={pointTerminologyOther}
        pointExpirations={loyaltyPointExpirations}
        timeZone={data.timeZone}
        readOnly
      />
      {isMembershipModalOpen ? (
        <WithRealSafeAreaInsets>
          <CurrentPackagesModal
            isOpen={isMembershipModalOpen}
            type="MEMBERSHIP"
            clubRef={data}
            onClose={() => setIsMembershipModalOpen(false)}
          />
        </WithRealSafeAreaInsets>
      ) : null}
      {isPackagesModalOpen ? (
        <WithRealSafeAreaInsets>
          <CurrentPackagesModal
            isOpen={isPackagesModalOpen}
            type="STANDARD"
            clubRef={data}
            onClose={() => setIsPackagesModalOpen(false)}
          />
        </WithRealSafeAreaInsets>
      ) : null}
    </>
  );
};

const ClubSettingsFragmentString = graphql`
  fragment ClubSettings_Club on Club
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    name
    clubPolicyLink
    supportsPublic
    timeZone @include(if: $patronLoyalty)
    squareLoyaltyProgram @include(if: $patronLoyalty) {
      status
      terminologyOne
      terminologyOther
      rewardTiers {
        id
        name
        points
      }
    }
    ...CurrentPackagesModal_club
    ...useClubHasMemberships_club
    ...useClubHasPackages_club
  }
`;

const ClubSettingsCustomerFragmentString = graphql`
  fragment ClubSettings_customer on Customer
  @argumentDefinitions(
    patronLoyalty: { type: "Boolean!", defaultValue: false }
  ) {
    ...useGetPackageNeedingAttention_customer
    ...GiftCardsModal_customer
    squareLoyaltyAccount @include(if: $patronLoyalty) {
      balance
      expiringPointDeadlines {
        id
        points
        expiresAt
      }
    }
    memberships: memberPackages(type: MEMBERSHIP) {
      count
    }
    packages: memberPackages(type: STANDARD) {
      count
    }
    rainchecks {
      count
    }
  }
`;
